import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, ScrollText, Swords, Sparkles, Dices,
  Shield, Settings, Menu, X, LogOut, BookOpen, Skull, Library, ShieldCheck,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/characters", label: "Characters", icon: Users },
  { to: "/campaigns", label: "Campaigns", icon: ScrollText },
  { to: "/compendium", label: "Compendium", icon: Library },
  { to: "/spells", label: "Spells", icon: Sparkles },
  { to: "/items", label: "Items & Gear", icon: Swords },
  { to: "/bestiary", label: "Bestiary", icon: Skull },
  { to: "/dice", label: "Dice Roller", icon: Dices },
  { to: "/dm", label: "DM View", icon: Shield },
  { to: "/rules", label: "Rules", icon: BookOpen },
  { to: "/admin", label: "Admin", icon: ShieldCheck, adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings },
];

function NavItems({ onNavigate, role }) {
  const items = NAV.filter((n) => !n.adminOnly || role === "admin");
  return (
    <nav className="flex flex-col gap-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? "bg-violet-500/15 text-white border border-violet-500/40 shadow-[0_0_20px_-8px_rgba(139,92,246,0.7)]"
                : "text-zinc-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <item.icon
                size={18}
                className={isActive ? "text-violet-300" : "text-zinc-500 group-hover:text-violet-300"}
              />
              {item.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.username || user?.email || "A").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[270px] shrink-0 flex-col gap-6 border-r border-white/[0.06] bg-[#0b0b10] px-5 py-6 fixed h-screen">
        <button onClick={() => navigate("/")} className="text-left">
          <Logo size="md" />
        </button>
        <div className="flex-1 overflow-y-auto">
          <NavItems />
        </div>
        <UserCard user={user} initials={initials} onLogout={logout} />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-16 border-b border-white/[0.06] bg-[#0b0b10]">
        <button onClick={() => navigate("/")}><Logo size="sm" /></button>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(true)}
          className="p-2 rounded-lg glass glass-hover"
        >
          <Menu size={20} />
        </button>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            />
            <motion.aside
              initial={{ x: -320 }} animate={{ x: 0 }} exit={{ x: -320 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="lg:hidden fixed left-0 top-0 z-50 h-full w-[280px] flex flex-col gap-6 border-r border-white/10 bg-[#0b0b10] px-5 py-6"
            >
              <div className="flex items-center justify-between">
                <Logo size="md" />
                <button onClick={() => setOpen(false)} className="p-2 rounded-lg glass" data-testid="mobile-close-btn">
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <NavItems onNavigate={() => setOpen(false)} />
              </div>
              <UserCard user={user} initials={initials} onLogout={logout} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <main className="flex-1 lg:ml-[270px] pt-16 lg:pt-0 min-h-screen w-full min-w-0 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 w-full">{children}</div>
      </main>
    </div>
  );
}

function UserCard({ user, initials, onLogout }) {
  return (
    <div className="glass rounded-2xl p-3 flex items-center gap-3" data-testid="user-card">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-violet-800 flex items-center justify-center font-display font-bold text-sm">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{user?.username || "Adventurer"}</div>
        <div className="text-[11px] text-zinc-500 truncate capitalize">{user?.role || "user"}</div>
      </div>
      <button
        onClick={onLogout}
        data-testid="logout-btn"
        className="p-2 rounded-lg text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 transition"
        title="Log out"
      >
        <LogOut size={17} />
      </button>
    </div>
  );
}
